import React, { ReactNode } from 'react';
import { SessionController } from '../controllers/SessionController';

interface Props {
    controller: SessionController
    children: JSX.Element | false | undefined | (JSX.Element | false | undefined)[],
    location: { pathname: string };
}

interface State {
    hasError: boolean,
    handledError: boolean,
    isTranslationError: boolean,
}

export default class ErrorBoundary extends React.Component<Props, State> {
    public state: State = {
        hasError: false,
        handledError: false,
        isTranslationError: false,
    };

    private recoveryTimer?: NodeJS.Timeout;

    constructor(props: Props) {
        super(props);
        this.state.hasError = false;
    }

    static getDerivedStateFromError(error: Error): any {
        // Check if this is a Google Translate related error
        const errorMessage = error?.message || '';
        const isTranslationError =
            errorMessage.includes('removeChild') ||
            errorMessage.includes('insertBefore') ||
            errorMessage.includes('The node to be removed is not a child of this node');

        // Update state so the next render will show the fallback UI.
        return { hasError: true, handledError: false, isTranslationError };
    }

    componentDidCatch(error: Error, componentError: any): void {
        const { controller, location } = this.props;
        if (this.state.handledError) return;

        // If this is a translation error, log it differently and attempt recovery
        if (this.state.isTranslationError) {
            console.warn('Google Translate DOM manipulation caused an error. Attempting recovery...', error);

            // Attempt to recover after a short delay
            this.recoveryTimer = setTimeout(() => {
                this.setState({ hasError: false, handledError: false, isTranslationError: false });
            }, 100);
        } else {
            // For other errors, handle normally
            controller.handleException(error, location.pathname, componentError);
        }

        this.setState({ handledError: true });
    }

    componentWillUnmount(): void {
        if (this.recoveryTimer) {
            clearTimeout(this.recoveryTimer);
        }
    }

    componentDidUpdate(prevProps: Props): void {
        // Reset error state when location changes
        if (this.state.hasError && this.props.location.pathname !== prevProps.location.pathname) {
            this.setState({ hasError: false, handledError: false, isTranslationError: false });
        }
    }

    render(): ReactNode {
        if (this.state.hasError) {
            // Show a friendlier message for Google Translate errors
            if (this.state.isTranslationError) {
                return (
                    <div className='white' style={{ padding: '20px', whiteSpace: 'pre-line' }}>
                        <h1>Page Translation Detected</h1>
                        <p>
                            It looks like this page was translated using Google Translate or a similar tool.
                            This can sometimes cause display issues with the app.
                        </p>
                        <p>Attempting to recover automatically...</p>
                        <p>
                            If the issue persists, try refreshing the page or disabling auto-translation for this site.
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            style={{ padding: '10px 20px', fontSize: '16px', cursor: 'pointer' }}
                        >
                            Refresh Page
                        </button>
                    </div>
                );
            }

            // You can render any custom fallback UI for other errors
            return <h1 className='white' style={{ whiteSpace: 'pre-line' }}>{`Something went wrong.
            
            Let Ralph know what you did to cause this and it will be fixed in a jiffy.
            
            Jolly Good.
            
            Tally Ho!`}</h1>;
        }

        return this.props.children;
    }
}